<template>
  <div class="user-management">
    <div class="toolbar">
      <div class="toolbar-left">
        <ButtonVue
          label="Создать пользователя"
          icon="pi pi-user-plus"
          @click="openCreateDialog"
        />
      </div>
      <div class="toolbar-right">
        <span class="summary" v-if="users.length">
          Всего пользователей: {{ users.length }}
        </span>
      </div>
    </div>

    <DataTable
      :value="users"
      dataKey="id"
      :loading="loading"
      class="p-datatable-sm"
    >
      <ColumnTable field="id" header="ID" style="width: 5rem" />
      <ColumnTable field="username" header="Имя пользователя" />
      <ColumnTable field="email" header="Email" />
      <ColumnTable field="role" header="Роль" style="width: 8rem" />
      <ColumnTable header="Доступы" style="min-width: 14rem">
        <template #body="{ data }">
          <div class="permissions-summary">
            <span v-if="userPermissionsSummary[data.id]?.length">
              {{ userPermissionsSummary[data.id].join(', ') }}
            </span>
            <span v-else class="muted">права не настроены</span>
          </div>
        </template>
      </ColumnTable>
      <ColumnTable header="Действия" style="width: 16rem">
        <template #body="{ data }">
          <div class="row-actions">
            <ButtonVue
              label="Пароль"
              :icon="data.hasPassword ? 'pi pi-lock' : 'pi pi-lock-open'"
              text
              rounded
              @click="resetPassword(data)"
            />
            <ButtonVue
              label="Права"
              icon="pi pi-shield"
              text
              rounded
              @click="openPermissionsDialog(data)"
            />
            <ButtonVue
              label="Удалить"
              icon="pi pi-trash"
              severity="danger"
              text
              rounded
              @click="deleteUser(data)"
            />
          </div>
        </template>
      </ColumnTable>
    </DataTable>

    <!-- Диалог создания пользователя -->
    <DialogVue
      v-model:visible="createDialogVisible"
      modal
      :style="{ width: 'min(520px, 96vw)' }"
      header="Создание пользователя"
    >
      <div class="form-grid">
        <div class="field">
          <label>Имя пользователя *</label>
          <InputText v-model="createForm.username" />
        </div>
        <div class="field">
          <label>Email *</label>
          <InputText v-model="createForm.email" />
        </div>
        <div class="field field-checkbox">
          <div class="flex align-items-center gap-2">
            <AppCheckbox
              id="create-is-admin"
              v-model="createForm.isAdmin"
              :binary="true"
            />
            <label for="create-is-admin">Администратор</label>
          </div>
        </div>
        <div class="field">
          <label>Пароль (необязательно)</label>
          <InputText
            v-model="createForm.password"
            type="password"
            placeholder="Если пусто — придёт письмо для установки"
          />
        </div>
      </div>

      <template #footer>
        <ButtonVue
          label="Отмена"
          severity="secondary"
          text
          @click="createDialogVisible = false"
        />
        <ButtonVue
          label="Создать"
          @click="submitCreate"
        />
      </template>
    </DialogVue>

    <!-- Диалог прав доступа -->
    <DialogVue
      v-model:visible="permissionsDialogVisible"
      modal
      :style="{ width: 'min(720px, 96vw)' }"
      :header="permissionsDialogTitle"
    >
      <div v-if="currentUser" class="permissions-dialog-body">
        <p class="muted">
          Отмеченные страницы будут доступны пользователю (право <b>view</b>).
        </p>

        <div class="permissions-list">
          <div
            v-for="page in pages"
            :key="page.id"
            class="permission-row"
          >
            <div class="permission-main">
              <AppCheckbox
                :inputId="`page-${page.id}`"
                :modelValue="hasViewPermission(page)"
                :binary="true"
                @update:modelValue="checked => togglePagePermission(page, checked)"
              />
              <label :for="`page-${page.id}`">
                {{ page.name }}
                <span v-if="page.url" class="permission-url">{{ page.url }}</span>
              </label>
            </div>
          </div>
        </div>
      </div>

      <template #footer>
        <ButtonVue
          label="Отмена"
          severity="secondary"
          text
          @click="permissionsDialogVisible = false"
        />
        <ButtonVue
          label="Сохранить"
          @click="savePermissions"
        />
      </template>
    </DialogVue>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import axios from 'axios'
import { useToast } from 'primevue/usetoast'
import { useUserStore } from '@/stores/user'

const userStore = useUserStore()
const toast = useToast()

const users = ref([])
const loading = ref(false)

const createDialogVisible = ref(false)
const permissionsDialogVisible = ref(false)

const createForm = ref({
  username: '',
  email: '',
  password: '',
  isAdmin: false
})

const currentUser = ref(null)
const pages = ref([])
const currentPermissions = ref([])

const permissionsDialogTitle = computed(() => {
  if (!currentUser.value) return 'Права доступа'
  return `Права доступа: ${currentUser.value.username}`
})

const userPermissionsSummary = computed(() => {
  const map = {}
  users.value.forEach((u) => {
    map[u.id] = (u.permissions || []).map((p) => p.pageName)
  })
  return map
})

async function loadUsers() {
  loading.value = true
  try {
    const { data } = await axios.get('https://ncht.bz/api/auth/users', {
      headers: {
        Authorization: `Bearer ${userStore.token}`
      }
    })
    users.value = data || []
  } catch (err) {
    console.error('[UserManagement] loadUsers error', err)
  } finally {
    loading.value = false
  }
}

function openCreateDialog() {
  createForm.value = {
    username: '',
    email: '',
    password: '',
    isAdmin: false
  }
  createDialogVisible.value = true
}

async function submitCreate() {
  if (!createForm.value.username?.trim() || !createForm.value.email?.trim()) {
    toast.add({ severity: 'warn', summary: 'Заполните имя и email', life: 3000 })
    return
  }

  try {
    await axios.post('https://ncht.bz/api/auth/new-user', {
      username: createForm.value.username.trim(),
      email: createForm.value.email.trim(),
      password: createForm.value.password || undefined,
      isAdmin: !!createForm.value.isAdmin
    }, {
      headers: {
        Authorization: `Bearer ${userStore.token}`
      }
    })

    createDialogVisible.value = false
    toast.add({ severity: 'success', summary: 'Пользователь создан', life: 3000 })
    await loadUsers()
  } catch (err) {
    console.error('[UserManagement] submitCreate error', err)
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: err.response?.data?.message || err.message,
      life: 5000
    })
  }
}

async function openPermissionsDialog(user) {
  currentUser.value = user
  permissionsDialogVisible.value = true
  try {
    const { data } = await axios.get(`https://ncht.bz/api/auth/users/${user.id}/permissions`, {
      headers: {
        Authorization: `Bearer ${userStore.token}`
      }
    })
    pages.value = data.pages || []
    currentPermissions.value = data.permissions || []
  } catch (err) {
    console.error('[UserManagement] openPermissionsDialog error', err)
    pages.value = []
    currentPermissions.value = []
  }
}

function hasViewPermission(page) {
  return currentPermissions.value.some(
    (p) => p.pageName === page.name && p.permissionName === 'view'
  )
}

function togglePagePermission(page, checked) {
  if (!checked) {
    currentPermissions.value = currentPermissions.value.filter(
      (p) => !(p.pageName === page.name && p.permissionName === 'view')
    )
  } else {
    if (!hasViewPermission(page)) {
      currentPermissions.value.push({
        pageName: page.name,
        permissionName: 'view'
      })
    }
  }
}

async function savePermissions() {
  if (!currentUser.value) return

  try {
    const payload = {
      permissions: currentPermissions.value.map((p) => ({
        pageName: p.pageName,
        permissionName: p.permissionName
      }))
    }

    const { data } = await axios.put(
      `https://ncht.bz/api/auth/users/${currentUser.value.id}/permissions`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${userStore.token}`
        }
      }
    )

    currentPermissions.value = data.permissions || []
    permissionsDialogVisible.value = false
    toast.add({ severity: 'success', summary: 'Сохранено', detail: 'Права доступа обновлены', life: 3000 })
    await loadUsers()
  } catch (err) {
    console.error('[UserManagement] savePermissions error', err)
    const msg = err.response?.data?.error || err.response?.data?.message || err.message
    toast.add({ severity: 'error', summary: 'Ошибка сохранения прав', detail: msg, life: 5000 })
  }
}

async function resetPassword(user) {
  try {
    const { data } = await axios.post(
      `https://ncht.bz/api/auth/users/${user.id}/reset-password`,
      {},
      {
        headers: {
          Authorization: `Bearer ${userStore.token}`
        }
      }
    )

    toast.add({
      severity: 'success',
      summary: 'Пароль сброшен',
      detail: data?.message || `Новый пароль отправлен пользователю ${user.username}`,
      life: 4000
    })
  } catch (err) {
    console.error('[UserManagement] resetPassword error', err)
    const msg = err.response?.data?.message || err.response?.data?.error || err.message
    toast.add({
      severity: 'error',
      summary: 'Ошибка сброса пароля',
      detail: msg,
      life: 6000
    })
  }
}

async function deleteUser(user) {
  if (!window.confirm(`Удалить пользователя "${user.username}"?`)) return
  try {
    await axios.delete(
      `https://ncht.bz/api/auth/users/${user.id}`,
      {
        headers: {
          Authorization: `Bearer ${userStore.token}`
        }
      }
    )
    toast.add({
      severity: 'success',
      summary: 'Пользователь удален',
      life: 2500
    })
    await loadUsers()
  } catch (err) {
    console.error('[UserManagement] deleteUser error', err)
    toast.add({
      severity: 'error',
      summary: 'Ошибка удаления',
      detail: err.response?.data?.message || err.message,
      life: 5000
    })
  }
}

loadUsers()
</script>

<style scoped>
.user-management {
  width: 100%;
}

.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.summary {
  font-size: 0.9rem;
  color: var(--p-text-muted-color, #6b7280);
}

.row-actions {
  display: flex;
  gap: 0.25rem;
  justify-content: flex-end;
}

.permissions-summary {
  font-size: 0.8rem;
}

.muted {
  color: var(--p-text-muted-color, #6b7280);
}

.permissions-dialog-body {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.permissions-list {
  max-height: 340px;
  overflow-y: auto;
  padding-right: 0.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.permission-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.4rem 0.25rem;
}

.permission-main {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.permission-main label {
  cursor: pointer;
  font-size: 0.9rem;
}

.permission-url {
  margin-left: 0.25rem;
  font-size: 0.8rem;
  color: var(--p-text-muted-color, #9ca3af);
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem 1rem;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.field label {
  font-size: 0.85rem;
  font-weight: 500;
}

@media (max-width: 768px) {
  .form-grid {
    grid-template-columns: 1fr;
  }
}
</style>

